import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import classNames from 'classnames';
import {
  Badge,
  Button,
  ChevronDownIcon,
  Divider,
  DocumentSearchIcon,
  DropdownItem,
  FileDragDropIcon,
  Grid,
  GridItem,
  HelpIcon,
  IconButton,
  InfoCard,
  MailSquareIcon,
  Select,
  SensitiveInfoIcon,
  Tab,
  TabBox,
  TabPanel,
  Tabs,
  TextField,
  ToolTip,
  Typography,
  useAlert,
  useToast,
} from 'fasoo-ui-component-next';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalStatusType,
  DetectedFileInfo,
  RequestApprovalDto,
} from 'src/api/user/approvalApi';
import encMailApprovalApi, { EncMailApprovalDetailEntity } from 'src/api/user/encMailApprovalApi';
import fileDownloadApi from 'src/api/user/fileDownloadApi';
import { CommonProps } from 'src/components/helpers/props';
import usePopup from 'src/hooks/usePopup';
import { getFasooUiComponentLanguage } from 'src/i18n';
import config from 'src/shared/config';
import { toLocalDateTimeLangStr } from 'src/shared/utils/localDateTime';
import {
  getIsManagerDownloadRemark,
  getMobileDownloadOptions,
} from 'src/shared/utils/sessionStorageManager';
import { base64ToUnicode } from 'src/shared/utils/textConverter';

import { ApprovalDetailFooter } from '../ApprovalDetailFooter';

import { ApprovalTypeIconArea } from './ApprovalTypeIconArea';
import { MailPreview } from './MailPreview';
import { openMobileViewer } from './openApp';
import RemarkPopup from './RemarkPopup';
import {
  AllApprovalDetailEntity,
  ApprovalDetailInfoPageType,
  ApprovalUserInfoDto,
  DETECTED_STATUS_LIST,
  DownloadHistoryInfo,
  FileIcon,
  ShowLineContent,
  UserDetail,
} from '.';

import './EncMailDetail.scss';

const FXM_API_URL = config.servers.fxm.url;
const mobileDownloadOptions = getMobileDownloadOptions();

function extractContent(s: string, space = false): string {
  const span = document.createElement('span');
  span.innerHTML = base64ToUnicode(s);
  if (space) {
    const children = span.querySelectorAll('*');
    for (let i = 0; i < children.length; i++) {
      if (children[i].textContent) {
        children[i].textContent += ' ';
      } else {
        (children[i] as HTMLElement).innerText += ' ';
      }
    }
  }
  return [span.textContent || span.innerText].toString().replace(/ +/g, ' ');
}

interface MailDetailInfoProps {
  encMailApproval: EncMailApprovalDetailEntity;
  handleOpenMailDetail: () => void;
  handleManualMailSend?: (approvalNo: string) => Promise<void>;
}
const MailDetailInfo: React.FC<MailDetailInfoProps> = ({
  encMailApproval,
  handleOpenMailDetail,
  handleManualMailSend,
}) => {
  const { t } = useTranslation(['menu', 'commons', 'userApproval', 'encMail']);
  return (
    <>
      <ShowLineContent
        classes={classNames('mail-detail-content')}
        content={
          <div className="mail-detail-root">
            <InfoCard
              classes="user-card-root"
              ellipsisContent
              startArea={<MailSquareIcon />}
              title={encMailApproval.subject}
              description={
                encMailApproval.contentType === 'text'
                  ? encMailApproval.content
                  : extractContent(encMailApproval.content, true)
              }
              endArea={
                <IconButton onClick={() => handleOpenMailDetail()}>
                  <DocumentSearchIcon />
                </IconButton>
              }
            />
          </div>
        }
      />
      {encMailApproval.to.length > 0 && (
        <ShowLineContent
          classes={classNames('mail-detail-recipient')}
          subTitle={t('userApproval:sendMail.receiver')}
          subTitleSize="md"
          contentStyle={{ padding: '0' }}
          content={
            <div className="mail-detail-root">
              {encMailApproval.to.map((v, i) => (
                <p className="mail-detail-receiver-item" key={`mail-to-${i}`}>
                  {v}
                </p>
              ))}
            </div>
          }
        />
      )}

      {encMailApproval.cc.length > 0 && (
        <ShowLineContent
          classes={classNames('mail-detail-recipient')}
          subTitle={t('userApproval:sendMail.cc')}
          subTitleSize="md"
          contentStyle={{ padding: '0' }}
          content={
            <div className="mail-detail-root">
              {encMailApproval.cc.map((v, i) => (
                <p className="mail-detail-receiver-item" key={`mail-cc-${i}`}>
                  {v}
                </p>
              ))}
            </div>
          }
        />
      )}
      <Divider classes="mg mt-16" />
      <ShowLineContent
        classes="mail-detail-option-root"
        title={
          <span className="sub-title-semi-grey">{t('userApproval:sendMail.mailSendType.$')}</span>
        }
        contentStyle={{ padding: '10px 0 0 0' }}
        content={
          <div className="mail-detail-root">
            {encMailApproval.isAutoSend && t('userApproval:sendMail.mailSendType.auto')}
            {encMailApproval.isAutoSend && encMailApproval.isManualSend && ', '}
            {encMailApproval.isManualSend && t('userApproval:sendMail.mailSendType.manual')}
          </div>
        }
      />
      <ShowLineContent
        classes="mail-detail-option-root"
        titleStyle={{ padding: '32px 0 0 0' }}
        title={
          <span className="sub-title-semi-grey">{t('userApproval:sendMail.sendSeparate')}</span>
        }
        contentStyle={{ padding: '10px 0 0 0' }}
        content={
          <div className="mail-detail-root">
            {encMailApproval.sendSeparate ? t('commons:use') : t('commons:nouse')}
          </div>
        }
      />
      <ShowLineContent
        classes="mail-detail-option-root"
        titleStyle={{ padding: '32px 0 0 0' }}
        title={<span className="sub-title-semi-grey">{t('userApproval:sendMail.priority.$')}</span>}
        contentStyle={{ padding: '10px 0 0 0' }}
        content={
          <div className="mail-detail-root">
            {encMailApproval.priority
              ? t('userApproval:sendMail.priority.use')
              : t('userApproval:sendMail.priority.nouse')}
          </div>
        }
      />
      <Divider classes="mg mt-24" />
      {/* {handleManualMailSend && (
        <MailManualSendPopup
          encMailApproval={encMailApproval}
          handleManualMailSend={handleManualMailSend}
          open={opened}
          onClose={closePopup}
        />
      )} */}
    </>
  );
};

export interface EncMailApprovalDetailProps extends CommonProps {
  encMailApproval: EncMailApprovalDetailEntity;
  ResetDetail: (detailEntity?: AllApprovalDetailEntity) => void;
  cancelApprovalChildren?: React.ReactNode;
  pageType?: ApprovalDetailInfoPageType;
  onCloseDetail: (open: boolean, isReject?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
}

export const EncMailDetail: React.FC<EncMailApprovalDetailProps> = ({
  encMailApproval,
  ResetDetail,
  cancelApprovalChildren,
  onCloseDetail,
  pageType = 'others',
  isComplete = false,
  selectedApproval,
}) => {
  const { t } = useTranslation(['menu', 'commons', 'userApproval', 'encMail']);
  const { alert } = useAlert();
  const { toastMsg } = useToast();
  const [tab, setTab] = useState(0);

  const openApprovalPopup = usePopup();
  const openRejectPopup = usePopup();
  const navigate = useNavigate();
  const locationVal = useLocation();
  const queryParams = new URLSearchParams(locationVal.search);

  const urlMailPreview = queryParams.get('mailPreview');

  const isManagerDownloadReason =
    (pageType === 'managerFile' || pageType === 'managerLog') && getIsManagerDownloadRemark();

  let approvers: ApprovalUserInfoDto[] = [];
  if (encMailApproval.completeApprovers.length > 0) {
    const completes = _.sortBy(encMailApproval.completeApprovers, 'step');
    approvers = approvers.concat(
      completes.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: approver.approvalStatus,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
          actorIp: approver.actorIp,
          approvalRemark: approver.approvalRemark,
          approvalDate: approver.updatedTs,
          method: approver.approvalMethod,
          otherDeptCode: approver.otherDeptCode === null ? null : approver.otherDeptCode,
          otherDeptName: approver.otherDeptName === null ? null : approver.otherDeptName,
          otherPositionCode:
            approver.otherPositionCode === null ? null : approver.otherPositionCode,
          otherPositionName:
            approver.otherPositionName === null ? null : approver.otherPositionName,
          otherRoleCode: approver.otherRoleCode === null ? null : approver.otherRoleCode,
          otherRoleName: approver.otherRoleName === null ? null : approver.otherRoleName,
          otherUserId: approver.otherUserId === null ? null : approver.otherUserId,
          otherUserName: approver.otherUserName === null ? null : approver.otherUserName,
        };
      })
    );
  }
  if (encMailApproval.progressApprovers.length > 0) {
    const progresses = _.sortBy(encMailApproval.progressApprovers, 'step');
    approvers = approvers.concat(
      progresses.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
    );
  }
  // 결재선이 다 끝나지 않는 단계
  // if (encMailApproval.currentStep < encMailApproval.approvalStep) {
  //   let postApprovalSteps: number[] = [];
  //   for (let i = encMailApproval.currentStep + 1; i <= encMailApproval.approvalStep; i++) {
  //     postApprovalSteps = postApprovalSteps.concat(i);
  //   }

  approvers = approvers.concat(
    encMailApproval.approvers
      .filter(
        (approver) =>
          // postApprovalSteps.includes(approver.step) &&
          approvers.filter((v) => v.step === approver.step)?.length === 0
      )
      .map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
  );
  // }

  const handleOpenApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.openPopup();
    } else if (type === 'reject') {
      openRejectPopup.openPopup();
    }
  };

  const handleCloseApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.closePopup();
    } else if (type === 'reject') {
      openRejectPopup.closePopup();
    }
  };

  const handleApprovalReject = (remark: string): void => {
    const popupType = openApprovalPopup.opened !== false ? 'approval' : 'reject';

    // console.log('얘가 작성한 remark : ', remark);
    if (encMailApproval.progressApprovers.length > 0) {
      const progressApproverInfo = encMailApproval.progressApprovers[0];
      const reqApprovalDto: RequestApprovalDto = {
        userId: progressApproverInfo.userId,
        approvalNo: encMailApproval.approvalNo,
        approvalMethod: encMailApproval.approvalMethod,
        approvalType: encMailApproval.approvalType,
        status: popupType === 'approval' ? 'APPROVAL' : 'REJECT',
        remark,
        proxyNo: selectedApproval?.progressApprovers[0].proxyNo
          ? selectedApproval.progressApprovers[0].proxyNo
          : null,
      };
      approvalDetailApi
        .approvalOrReject(reqApprovalDto)
        .then(() => {
          onCloseDetail(false, true);
        })
        .catch((err) => alert(err, 'error', '', undefined, true));
    } else {
      onCloseDetail(false);
    }

    handleCloseApprovalPopup(popupType);
  };

  //  사용 로그 선택 필터
  const [usageLogType, setUsageLogType] = useState<'ALL' | 'MAIL' | 'DOWNLOAD'>('ALL');
  const ChangeUsageLogType = (type: 'ALL' | 'MAIL' | 'DOWNLOAD'): void => {
    setUsageLogType(type);
  };

  const openMailDetail = (): void => {
    if (isComplete) {
      navigate(
        config.url(`/approval/complete?approvalNo=${selectedApproval?.approvalNo}&mailPreview=true`)
      );
    } else {
      navigate(
        config.url(`/approval/pending?approvalNo=${selectedApproval?.approvalNo}&mailPreview=true`)
      );
    }
  };

  const closeMailDetail = (): void => {
    navigate(-1);
  };

  // 수동 메일 전송
  const handleManualMailSend = async (approvalNo: string): Promise<void> => {
    try {
      await encMailApprovalApi.send(approvalNo).then(() => {
        toastMsg(uuidv4(), t('userApproval:sendMail.message.success'), 'success');
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      toastMsg(uuidv4(), t('userApproval:sendMail.message.error'), 'error');
      // alert(error as any, 'error');
    }
  };
  // 개인 정보 검출
  const [selectedDetectFile, setSelectedDetectFile] = useState<string | null>('');
  const [detectedSearchList, setDetectedSearchList] = useState<DetectedFileInfo[]>([]);
  const [detectInput, setDetectedInput] = useState<string>('');

  const debouncedHandleChangeSearchText = _.debounce((str: string) => {
    if (!encMailApproval.detectedFiles) {
      return;
    }
    if (str.length > 0) {
      const res = encMailApproval.detectedFiles.filter((item) => item.fileName.includes(str));
      setDetectedSearchList(res);
    } else {
      setDetectedSearchList(encMailApproval.detectedFiles);
    }
  }, 300);

  const handleChangeDetectedInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setDetectedInput(e.target.value);
    debouncedHandleChangeSearchText(e.target.value);
  };

  const handleSelectInput = (eventKey: string): void => {
    if (!encMailApproval.detectedFiles) {
      return;
    }
    encMailApproval.detectedFiles.forEach((element) => {
      if (element.fileUuid === eventKey) {
        setDetectedInput(element.fileName);
        setSelectedDetectFile(eventKey);
      }
    });
  };

  const detectedFilesLen = (type: 'pattern' | 'sensitive'): number => {
    if (!encMailApproval || !encMailApproval.detectedFiles) {
      return 0;
    }
    const fileInfo = encMailApproval.detectedFiles.find(
      (file) => file.fileUuid === selectedDetectFile
    );
    if (!fileInfo) {
      return 0;
    }
    if (type === 'pattern') {
      return fileInfo.totalPatternCount;
    }
    if (type === 'sensitive') {
      return fileInfo.totalPatternDetailCount;
    }
    return 0;
  };

  // 파일 다운로드(fasoo view, fasoo view+, secure view)
  const handleFileDownload = (approvalNo: string, fileUuid: string): void => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = /android/.test(userAgent);
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    if (isAndroid || isIOS) {
      fileDownloadApi
        .prepareDownload(approvalNo, fileUuid)
        .then(({ uuid }) => {
          const downloadUrl = encodeURIComponent(
            `${document.location.origin}${`${FXM_API_URL}/api/download/moriginal?uuid=${uuid}`}`
          );
          openMobileViewer(downloadUrl, getFasooUiComponentLanguage());
        })
        .catch((err) => {
          console.log('err', err);
        });
    } else {
      console.log('Is not mobile (Android, IOS)');
    }
  };

  return (
    <div className="enc-mail-detail-root">
      {!urlMailPreview && (
        <>
          <div
            className={classNames('enc-mail-detail-maincontainer', {
              'mg mb-85': isComplete === false,
              // 'mg mb-32': isComplete === true,
            })}
          >
            <ApprovalTypeIconArea
              onClose={onCloseDetail}
              type={encMailApproval.approvalType}
              status={encMailApproval.status}
            />
            <TabBox tabWidth="full" line classes="enc-mail-detail-root">
              <Tabs
                value={tab}
                onChange={(selected) => {
                  setTab(selected);
                  if (selected === 1) {
                    ResetDetail();
                  }
                }}
              >
                <Tab label={t('userApproval:tab.basicInfo')} index={0} />
                <Tab label={t('userApproval:tab.usageLogs')} index={1} />
              </Tabs>
              <TabPanel value={tab} index={0}>
                {!encMailApproval.fileDetails || (
                  <ShowLineContent title={t('userApproval:fileDetails.$')}>
                    <Grid spacing={0}>
                      <GridItem xs={12} padding="24px 0px 0px 0px">
                        <Grid spacing={0}>
                          <GridItem
                            classes={classNames('enc-mail-file-title')}
                            xs={6}
                            alignSelf="center"
                          >
                            <Typography variant="h3">{t('userApproval:fileDetails.$')}</Typography>
                            <span className="file-count">
                              {' '}
                              {encMailApproval.fileDetails.length}
                            </span>
                          </GridItem>
                        </Grid>
                      </GridItem>
                      <GridItem xs={12} classes={classNames('files-overflow')}>
                        {encMailApproval.fileDetails.map((fileDetail, fileIdx) => (
                          <div
                            key={`file-${fileIdx}`}
                            className="pt-10 flex"
                            onClick={() => {
                              if (mobileDownloadOptions.useDownload) {
                                handleFileDownload(fileDetail.approvalNo, fileDetail.fileUuid);
                              }
                            }}
                            onTouchStart={() => {
                              if (mobileDownloadOptions.useDownload) {
                                handleFileDownload(fileDetail.approvalNo, fileDetail.fileUuid);
                              }
                            }}
                          >
                            {FileIcon(fileDetail.fileName)}
                            <div className="download-file-name">{fileDetail.fileName}</div>
                          </div>
                        ))}
                      </GridItem>
                    </Grid>
                  </ShowLineContent>
                )}
                {!encMailApproval.approvalMethod || (
                  <ShowLineContent
                    classes={classNames('approval-method')}
                    title={t('userApproval:approvalMethod.$')}
                    content={t(`userApproval:approvalMethod.${encMailApproval.approvalMethod}`)}
                  />
                )}
                {/* 신청자 */}
                <ShowLineContent
                  title={t('userApproval:applicant')}
                  content={
                    <UserDetail
                      type="request"
                      dateStr={toLocalDateTimeLangStr(
                        encMailApproval.applyDate,
                        getFasooUiComponentLanguage()
                      )}
                      remark={encMailApproval.remark}
                      userInfo={{
                        userId: encMailApproval.userId,
                        userName: encMailApproval.userName,
                        positionName: encMailApproval.positionName,
                        deptName: encMailApproval.deptName,
                        actorIp: encMailApproval.actorIp,
                      }}
                      submission={encMailApproval.submission}
                    />
                  }
                />
                {encMailApproval.detectedFiles && encMailApproval.detectedFiles.length > 0 && (
                  <ShowLineContent
                    title={
                      <>
                        <div className="flex align-center">
                          {t('userApproval:secDataDetection')}
                        </div>
                        <div className="sec-description">
                          {DETECTED_STATUS_LIST.fst.map((element) => (
                            <div className="detect-description-element">
                              <div className="detect-status">
                                <span className={`detect-status-${element}`} />
                              </div>
                              {t(`userApproval:sensitiveData.status.${element}`)}
                            </div>
                          ))}
                        </div>
                        <div className="sec-description">
                          {DETECTED_STATUS_LIST.snd.map((element) => (
                            <div className="detect-description-element">
                              <div className="detect-status">
                                <span className={`detect-status-${element}`} />
                              </div>
                              {t(`userApproval:sensitiveData.status.${element}`)}
                              {element === 'ERROR' && (
                                <ToolTip
                                  classes="mg ml-4"
                                  as="div"
                                  message={
                                    <span className="fs-12">
                                      {t(`userApproval:sensitiveData.status.ERRORHelp`)}
                                    </span>
                                  }
                                  horiozionPosition="center"
                                >
                                  <HelpIcon />
                                </ToolTip>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    }
                    content={
                      <>
                        <Select
                          classes={classNames('sec-select-root')}
                          onClick={handleSelectInput}
                          selectedEventKey={selectedDetectFile || 'default-key'}
                          size="mds"
                          // width={260}
                          isTextInput
                          // key={}
                          fullWidth
                          textInput={
                            <TextField
                              value={detectInput}
                              onChange={handleChangeDetectedInput}
                              size="sm"
                              placeholder={t('userApproval:sensitiveData.placeholder')}
                              endIcon={
                                <IconButton>
                                  <ChevronDownIcon />
                                </IconButton>
                              }
                            />
                          }
                        >
                          {detectedSearchList.length === 0
                            ? encMailApproval.detectedFiles.map((file) => {
                                return (
                                  <DropdownItem
                                    severity={
                                      file.detectStatus === 'DETECTED' ? undefined : 'disabled'
                                    }
                                    eventKey={file.fileUuid}
                                  >
                                    <div className="flex">
                                      <div className="detect-status">
                                        <span className={`detect-status-${file.detectStatus}`} />
                                      </div>

                                      {file.fileName}
                                    </div>
                                  </DropdownItem>
                                );
                              })
                            : detectedSearchList.map((file) => {
                                return (
                                  <DropdownItem
                                    severity={
                                      file.detectStatus === 'DETECTED' ? undefined : 'disabled'
                                    }
                                    eventKey={file.fileUuid}
                                  >
                                    <div className="flex">
                                      <div className="detect-status">
                                        <span className={`detect-status-${file.detectStatus}`} />
                                      </div>

                                      {file.fileName}
                                    </div>
                                  </DropdownItem>
                                );
                              })}
                          {/* {selectedDetectFile === null && (
                        <DropdownItem eventKey="default-key" classes="dummy-dropdown">
                          <Typography variant="p" color="description">
                            {'문서를 선택해 주세요'}
                          </Typography>
                        </DropdownItem>
                      )} */}
                        </Select>
                        {selectedDetectFile !== null && (
                          <Typography variant="p" color="secondary" classes="mg mt-12">
                            <Trans i18nKey="userApproval:totalPattern">
                              <span />
                              <span />
                              <span className="file-count">
                                {{ n: detectedFilesLen('pattern') }}
                              </span>
                              <span />
                            </Trans>
                          </Typography>
                        )}
                        {selectedDetectFile !== null && (
                          <Typography variant="p" color="secondary" classes="mg mb-8">
                            <Trans i18nKey="userApproval:totalPatternInfoCount">
                              <span />
                              <span />
                              <span className="file-count">
                                {{ n: detectedFilesLen('sensitive') }}
                              </span>
                              <span />
                            </Trans>
                          </Typography>
                        )}
                        {encMailApproval.detectedFiles
                          .find((file) => file.fileUuid === selectedDetectFile)
                          ?.detectedInfos.map((info) => (
                            <div
                              key={info.patternId}
                              className={classNames('sensitive-info-row')}
                              // onClick={(event) => handleSelectSensitiveInfoItem(event, info)}
                            >
                              <SensitiveInfoIcon />
                              <div className="sensitive-info-row-Item">
                                <div>{info.patternName}</div>
                                <div className="sensitive-info-row-count">{info.detectedCount}</div>
                              </div>
                            </div>
                          ))}
                      </>
                    }
                  />
                )}
                {/* 메일 상세 정보 */}
                <div className="approval-detail-info-title">
                  <div className="title-text">{t('userApproval:sendMail.$')}</div>
                </div>
                <MailDetailInfo
                  encMailApproval={encMailApproval}
                  handleOpenMailDetail={openMailDetail}
                  handleManualMailSend={
                    pageType === 'userApprovalComplete' && encMailApproval.isManualSend
                      ? handleManualMailSend
                      : undefined
                  }
                />
                {/* 결재 상세 정보 */}
                <div className="approval-detail-info-title">
                  <div className="title-text">{t('userApproval:approvalDetail.$')}</div>
                </div>
                <ShowLineContent
                  title=""
                  content={
                    <>
                      {approvers.map((approver, idx) => {
                        return (
                          <UserDetail
                            step={approvers.length > 1 ? approver.step : undefined}
                            type="approver"
                            dateStr={
                              approver.approvalDate
                                ? toLocalDateTimeLangStr(
                                    approver.approvalDate,
                                    getFasooUiComponentLanguage()
                                  )
                                : null
                            }
                            remark={approver.approvalRemark ? approver.approvalRemark : null}
                            userInfo={approver}
                            key={`approver-${idx}`}
                          />
                        );
                      })}

                      {approvers.length <= 1 || <Divider />}
                    </>
                  }
                />
                {/* 상태 */}
                {/* {encMailApproval.status !== ApprovalStatusType.APPLY && (
                  <ShowLineContent
                    title={t('userApproval:status.$')}
                    content={
                      <div className="approval-status">
                        <span className={`approval-status-${encMailApproval.status}`} />
                        {t(`userApproval:status.${encMailApproval.status}`)}
                      </div>
                    }
                  />
                )} */}
                {/* 결재(취소) 일시 */}
                {/* {encMailApproval.updatedTs && (
                  <ShowLineContent
                    subTitle={t(`userApproval:updateStatusDate.${encMailApproval.status}`)}
                    content={
                      <div>
                        {toLocalDateTimeLangStr(
                          encMailApproval.updatedTs,
                          getFasooUiComponentLanguage()
                        )}
                      </div>
                    }
                  />
                )} */}
                {!cancelApprovalChildren || (
                  <>
                    <div className="horizon-blank horizon-blank-32" />
                    {approvers.length > 1 || <Divider />}
                    {cancelApprovalChildren}
                  </>
                )}
              </TabPanel>
              <TabPanel value={tab} index={1} classes={classNames('enc-mail-usage-log')}>
                <div className="log-button-wrapper">
                  <Button
                    size="sm"
                    pill
                    color={usageLogType === 'ALL' ? 'primary' : 'secondary'}
                    onClick={() => ChangeUsageLogType('ALL')}
                  >
                    {t('commons:all')}
                  </Button>
                  <Button
                    size="sm"
                    pill
                    color={usageLogType === 'MAIL' ? 'primary' : 'secondary'}
                    onClick={() => ChangeUsageLogType('MAIL')}
                  >
                    {t('userApproval:log.mailLog')}
                  </Button>
                  <Button
                    size="sm"
                    pill
                    color={usageLogType === 'DOWNLOAD' ? 'primary' : 'secondary'}
                    onClick={() => ChangeUsageLogType('DOWNLOAD')}
                  >
                    {t('userApproval:log.downloadLog')}
                  </Button>
                </div>
                {/* 메일 전송 이력 */}
                {(usageLogType === 'ALL' || usageLogType === 'MAIL') && (
                  <div className={classNames('mg mb-4 usage-title')}>
                    {t('userApproval:log.mailLog')}
                  </div>
                )}
                {(usageLogType === 'ALL' || usageLogType === 'MAIL') &&
                  encMailApproval.sendMails.map((v) => {
                    const mailSendTypes = [];
                    if (encMailApproval.isAutoSend) {
                      mailSendTypes.push(t('userApproval:sendMail.mailSendType.autoSend'));
                    }
                    if (encMailApproval.isManualSend) {
                      mailSendTypes.push(t('userApproval:sendMail.mailSendType.manualSend'));
                    }

                    const toList = v.to.split(';');
                    const firstTo = toList[0];
                    const otherNum = toList.length - 1;
                    return (
                      <div
                        className="usage-mail-send-log"
                        key={v.mailNo}
                        // onClick={(ev) => {
                        //   setSendMailButton(ev.currentTarget);
                        //   handleOpenSendMail(v);
                        // }}
                      >
                        <InfoCard
                          startArea={<MailSquareIcon />}
                          title={toLocalDateTimeLangStr(v.updatedTs, getFasooUiComponentLanguage())}
                          description={
                            <p>
                              {firstTo}
                              {otherNum > 0 && (
                                <Trans i18nKey="userApproval:sendMail.otherNum">
                                  {{ n: otherNum }}
                                </Trans>
                              )}
                            </p>
                          }
                          endArea={
                            <div>
                              <Badge
                                classes={classNames('send-status-badge')}
                                color={
                                  v.sendStatus === 'OK'
                                    ? 'green'
                                    : v.sendStatus === 'ERR'
                                    ? 'red'
                                    : 'gray'
                                }
                                text={t(`userApproval:sendMail.sendStatus.${v.sendStatus}`)}
                              />
                            </div>
                          }
                          ellipsisContent
                        />
                      </div>
                    );
                  })}
                {(usageLogType === 'ALL' || usageLogType === 'MAIL') &&
                  encMailApproval.sendMails.length === 0 && (
                    <div className={classNames(`download-history-root`)}>
                      <div style={{ height: '32px' }} />
                      <FileDragDropIcon />
                      <div style={{ height: '32px' }} />
                      <Typography variant="p" color="secondary">
                        {t('userApproval:message.noMailHistory')}
                      </Typography>
                    </div>
                  )}
                {(usageLogType === 'ALL' || usageLogType === 'DOWNLOAD') && (
                  <div
                    className={classNames('mg mb-4 usage-title', {
                      ' mt-32': usageLogType === 'ALL',
                    })}
                  >
                    {t('userApproval:log.downloadLog')}
                  </div>
                )}
                {(usageLogType === 'ALL' || usageLogType === 'DOWNLOAD') &&
                  encMailApproval.downloadHistory.map((v, i) => {
                    const fileName = encMailApproval.fileDetails.find(
                      (file) => file.fileUuid === v.fileUuid
                    )?.fileName;
                    return (
                      <DownloadHistoryInfo
                        fileName={fileName || ''}
                        updatedTsStr={toLocalDateTimeLangStr(
                          v.updatedTs,
                          getFasooUiComponentLanguage()
                        )}
                        userName={v.userName}
                        userId={v.userId}
                        userIpText={v.userIpText}
                        action={v.action}
                        adminRemark={isManagerDownloadReason ? v.adminRemark || '' : null}
                        key={`dh-${i}`}
                      />
                    );
                  })}
                {(usageLogType === 'ALL' || usageLogType === 'DOWNLOAD') &&
                  encMailApproval.downloadHistory.length === 0 && (
                    <div className={classNames(`download-history-root`)}>
                      <div style={{ height: '32px' }} />
                      <FileDragDropIcon />
                      <div style={{ height: '32px' }} />
                      <Typography variant="p" color="secondary">
                        {t('userApproval:message.noFileHistory')}
                      </Typography>
                    </div>
                  )}
              </TabPanel>
            </TabBox>
          </div>
          {isComplete === false && (
            <ApprovalDetailFooter
              approvalMethod={encMailApproval.approvalMethod}
              handleApprove={() => handleOpenApprovalPopup('approval')}
              handleReject={() => handleOpenApprovalPopup('reject')}
            />
          )}

          {(openApprovalPopup.opened === true || openRejectPopup.opened === true) && (
            <RemarkPopup
              open={
                openApprovalPopup.opened === true
                  ? openApprovalPopup.opened
                  : openRejectPopup.opened
              }
              entity={encMailApproval}
              onSubmit={handleApprovalReject}
              onClose={handleCloseApprovalPopup}
              isReject={openApprovalPopup.opened !== true}
            />
          )}
        </>
      )}
      {urlMailPreview && (
        <MailPreview
          encMailApproval={encMailApproval}
          exchangeFullApproval={null}
          onClose={closeMailDetail}
        />
      )}
    </div>
  );
};

export default EncMailDetail;
